// ─── src/api/gewFeedback.ts ──────────────────────────────────────────────────
//
// GEW 2026 attendee feedback (the 13-question form sent from the portals).
// Staff reads go through the API key; catalyst reads/sends go through the
// catalyst JWT client and only reach the catalyst's own meetups.

import axios from "axios";
import { catalystClient } from "./catalystPortal";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? "https://api.startupsyria.org/api/v1";

const adminClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { "x-api-key": process.env.REACT_APP_DASHBOARD_API_KEY ?? "" },
});

export type FeedbackSource = "gew_event" | "meetup";

export interface RatingStats {
  average: number | null;
  answered: number;
  /** Counts for 1..5. */
  distribution: number[];
}

export interface FeedbackSummary {
  emailed: number;
  responses: number;
  responseRate: number | null;
  ratings: {
    ratingOverall: RatingStats;
    ratingContent: RatingStats;
    ratingSpeakers: RatingStats;
    ratingOrganization: RatingStats;
  };
  nps: {
    score: number | null;
    average: number | null;
    promoters: number;
    passives: number;
    detractors: number;
    /** Counts for 0..10. */
    distribution: number[];
  };
  speakersNotApplicable: number;
  nextSteps: Record<string, number>;
  hearAbout: Record<string, number>;
  quotes: number;
}

export interface FeedbackEventInfo {
  sourceType: FeedbackSource;
  id: string;
  title: string;
  organizerName: string;
  eventDate: string | null;
  endDate: string | null;
  startTime: string | null;
  mode: string | null;
  governorate: string | null;
  city: string | null;
  venue: string | null;
  type: string | null;
  track: string | null;
}

export interface FeedbackEventRow extends FeedbackEventInfo {
  emailed: number;
  responses: number;
  responseRate: number | null;
  ratingOverall: number | null;
  ratingContent: number | null;
  ratingSpeakers: number | null;
  ratingOrganization: number | null;
  npsScore: number | null;
}

export interface FeedbackOverview {
  summary: FeedbackSummary;
  events: FeedbackEventRow[];
  orphanResponses: number;
}

export interface FeedbackAnswers {
  id: string;
  submittedAt: string;
  ratingOverall: number;
  nps: number;
  mostUseful: string | null;
  ratingContent: number;
  ratingSpeakers: number | null;
  speakersNotApplicable: boolean;
  ratingOrganization: number;
  nextSteps: string[];
  nextStepOther: string | null;
  wantedTopics: string | null;
  improvement: string | null;
  quoteConsent: boolean;
  quote: string | null;
  quoteName: string | null;
  hearAbout: string;
  hearAboutOther: string | null;
  notes: string | null;
  attendee: { fullName: string; email: string; whatsapp: string } | null;
}

export interface FeedbackExportRow extends FeedbackAnswers {
  event: FeedbackEventInfo | null;
}

export interface FeedbackSendStatus {
  /** Attendees checked in at the door (the only people who get the form). */
  eligible: number;
  submitted: number;
  recentlySent: number;
  toSend: number;
  emailed: number;
  lastSentAt: string | null;
  eventStarted: boolean;
  canSend: boolean;
}

export interface EventFeedback {
  event: FeedbackEventInfo;
  summary: FeedbackSummary;
  send: FeedbackSendStatus;
  responses: FeedbackAnswers[];
}

export interface FeedbackQuote {
  id: string;
  quote: string;
  name: string | null;
  ratingOverall: number;
  submittedAt: string;
  event: { title: string; organizerName: string; city: string | null } | null;
}

// ── Staff ────────────────────────────────────────────────────────────────────
export const getFeedbackOverview = async () =>
  (await adminClient.get<FeedbackOverview>("/gew-feedback/admin/overview")).data;

export const getEventFeedbackAdmin = async (sourceType: FeedbackSource, id: string) =>
  (await adminClient.get<EventFeedback>(`/gew-feedback/admin/events/${sourceType}/${id}`)).data;

export const sendFeedbackAdmin = async (sourceType: FeedbackSource, id: string) =>
  (
    await adminClient.post<{ queued: number; message: string }>(
      `/gew-feedback/admin/events/${sourceType}/${id}/send`,
    )
  ).data;

export const getFeedbackResponses = async (sourceType?: FeedbackSource, eventId?: string) =>
  (
    await adminClient.get<FeedbackExportRow[]>("/gew-feedback/admin/responses", {
      params: { sourceType, eventId },
    })
  ).data;

export const getFeedbackQuotes = async () =>
  (await adminClient.get<FeedbackQuote[]>("/gew-feedback/admin/quotes")).data;

// ── Catalyst (own meetups only) ──────────────────────────────────────────────
export const getCatalystEventFeedback = async (eventId: string) =>
  (await catalystClient.get<EventFeedback>(`/catalyst-portal/events/${eventId}/feedback`)).data;

export const sendCatalystFeedback = async (eventId: string) =>
  (
    await catalystClient.post<{ queued: number; message: string }>(
      `/catalyst-portal/events/${eventId}/feedback/send`,
    )
  ).data;
